'use client'

import { useFieldActions, useForm } from 'form-atoms'
import { useAtom } from 'jotai'
import { useQueryStates } from 'nuqs'
import type { PropsWithChildren } from 'react'
import { useEffect, useMemo } from 'react'
import { useEffectOnce } from 'react-use'
import {
  fromSearchParamsParsers,
  toSearchParamsParsers,
} from '@/src/stores/params'
import {
  type BalanceInputToken,
  fromBalanceEffectAtom,
  type InputToken,
  swapFormAtom,
} from '@/src/stores/swap/atoms'

interface SwapFormHydrationProps {
  from?: BalanceInputToken
  to?: InputToken
}

export const SwapFormHydration = (
  props: PropsWithChildren<SwapFormHydrationProps>,
) => {
  const { from: defaultFrom, to: defaultTo, children } = props
  const { fieldAtoms } = useForm(swapFormAtom)
  const { setValue: setFromValue } = useFieldActions(fieldAtoms.from)
  const { setValue: setToValue } = useFieldActions(fieldAtoms.to)
  const { reset: resetFromAmount, setValue: setFromAmountValue } =
    useFieldActions(fieldAtoms.fromAmount)
  /**
   * We also need to keep in sync our client state with query state,
   * because page.tsx or server component that use this SwapFormHydration
   * are usually executed once on the server side, but then if query params change,
   * on the client side, we need to update the form or we'll get the last server state,
   * instead of last client state.
   */
  const [{ fromChain, fromToken, fromAmount }] = useQueryStates(
    fromSearchParamsParsers,
  )
  const [{ toChain, toToken }] = useQueryStates(toSearchParamsParsers)

  const from = useMemo(
    () =>
      fromChain && fromToken
        ? { chain: fromChain, token: fromToken }
        : defaultFrom,
    [fromChain, fromToken, defaultFrom],
  )
  const to = useMemo(
    () => (toChain && toToken ? { chain: toChain, token: toToken } : defaultTo),
    [toChain, toToken, defaultTo],
  )

  /**
   * When we mount our swap form we need to reset and hydrate fields.
   * This happens in an effect to avoid updating state during render.
   */
  useEffectOnce(() => {
    resetFromAmount()

    // Hydrate initial values
    if (from) {
      setFromValue(from)
    }
    if (to) {
      setToValue(to)
    }
    if (fromAmount) {
      setFromAmountValue(fromAmount)
    }
  })

  /**
   * Respond to URL parameter changes to update form values
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: setFromValue and setToValue are stable from form-atoms
  useEffect(() => {
    /**
     * If there is a specific from token provided in the url we should use it as a value
     */
    if (fromChain && fromToken) {
      setFromValue(from)
    }

    /**
     * If there is a specific to token provided in the url we should use it as a value
     */
    if (toChain && toToken) {
      setToValue(to)
    }
  }, [fromChain, fromToken, toChain, toToken, from, to]) // Dependencies match logic: raw params + memoized values for setValue calls

  /**
   * If we switch a wallet we update the balance so we also need to revalidate
   * our form
   */
  useAtom(fromBalanceEffectAtom)

  return children
}
